import React, { useEffect, useState } from "react";
import * as yup from "yup";
import { DialogActions, IconButton, InputAdornment } from "@material-ui/core";
import { Formik } from "formik";
import Swal from "sweetalert2";
import { CustomTextField } from "../../../../partials/content/Forms";
import {
  FormDateField,
  FormTextField,
} from "../../../../partials/content/Forms/FormField";
import { onPreventEnterSubmitting, setObject } from "../../../../utils/utils";
import DialogLayout from "../../../../partials/content/DialogLayout";
import SearchCurrency from "../../setup/finance/searchCurrency";
import { Search } from "@mui/icons-material";
import { saveExchangeRate } from "../../../../services/finance/finance";
import store from "../../../../store/store";
import { useGlobalData } from "../../../../../provider/GlobalDataProvider";
import { DateTime } from "luxon";
const exchangeRateFormSchema = yup.object().shape({
  ID: yup.number().default(0),
  exCurrCode: yup.string().required("From Currency is required"),
  toCurrCode: yup.string().required("To Currency is required"),
  exRateToBaseCurr: yup
    .number()
    .transform((value) => (isNaN(value) ? 0 : value))
    .nullable()
    .required("Exchange Rate To Base Currency is required")
    .moreThan(0, "Exchange Rate To Base Currency cannot be zero or empty")
    .default(0),
  exRateToForeignCurr: yup
    .number()
    .transform((value) => (isNaN(value) ? 0 : value))
    .nullable()
    .moreThan(0, "Exchange Rate To Base Currency cannot be zero or empty")
    .required("Exchange Rate To Base Currency is required")
    .default(0),
});
const ExchangeRateForm = ({ exchangeRate, onConfirmSave, onCloseDialog }) => {
  const [openSearchCurrency, setOpenSearchCurrency] = useState(false);
  const { companyProfile, socket } = useGlobalData();
  const [exchangeRateData, setExchangeRateData] = useState({
    ID: 0,
    exCurrID: 0,
    exCurrCode: "",
    toCurrID: companyProfile.currency.ID,
    toCurrCode: companyProfile.currency.currencyCode,
    dateValidFrom: DateTime.now().startOf("day"),
    dateValidTo: DateTime.now().endOf("day"),
    exchangeRateStatus: 0,
    exchangeRateStatusDesc: "",
    exRateToBaseCurr: 0,
    exRateToForeignCurr: 0,
    CreatedAt: DateTime.now(),
    UpdatedAt: DateTime.now(),
    createdBy: 0,
    updatedBy: 0,
  });
  useEffect(() => {
    const { auth } = store.getState();

    if (exchangeRate && exchangeRate.ID > 0) {
      setObject(socket, "lock", exchangeRate.ID, "EXRT", auth.user)
        .then((response) => {
          setExchangeRateData({
            ID: exchangeRate.ID,
            exCurrID: exchangeRate.exCurrID,
            exCurrCode: exchangeRate.exCurrCode,
            toCurrID: exchangeRate.toCurrID,
            toCurrCode: exchangeRate.toCurrCode,
            dateValidFrom: exchangeRate.dateValidFrom,
            dateValidTo: exchangeRate.dateValidTo,
            exchangeRateStatus: exchangeRate.exchangeRateStatus,
            exchangeRateStatusDesc: exchangeRate.exchangeRateStatusDesc,
            exRateToBaseCurr: exchangeRate.exRateToBaseCurr,
            exRateToForeignCurr: exchangeRate.exRateToForeignCurr,
            CreatedAt: exchangeRate.CreatedAt,
            UpdatedAt: exchangeRate.UpdatedAt,
            createdBy: exchangeRate.createdBy,
            updatedBy: exchangeRate.updatedBy,
          });
        })
        .catch((error) => {
          onCloseDialog();
        });
    }
    return () => {
      if (exchangeRate && exchangeRate.ID > 0) {
        setObject(socket, "release", exchangeRate.ID, "EXRT", auth.user);
      }
    };
  }, [exchangeRate]);
  return (
    <React.Fragment>
      <Formik
        initialValues={exchangeRateData}
        validationSchema={exchangeRateFormSchema}
        enableReinitialize={true}
        onSubmit={async (values, { setStatus, setSubmitting }) => {
          values.exRateToBaseCurr = parseFloat(values.exRateToBaseCurr);
          values.exRateToForeignCurr = parseFloat(values.exRateToForeignCurr);
          Swal.mixin({
            customClass: {
              confirmButton: "btn btn-success btn-md btn-pill btn-bold",
              cancelButton: "btn btn-danger btn-md btn-pill btn-bold",
            },
            buttonsStyling: false,
          })
            .fire({
              title: "Are you sure want to save this Exchange Rate ?",
              text: "Please note that you are responsible for this action",
              icon: "question",
              showCloseButton: true,
              showCancelButton: true,
              confirmButtonText: `<i class="fas fa-check-circle"></i> Yes, save it`,
              cancelButtonText: `<i class="fas fa-times-circle"></i> No, cancel`,
              reverseButtons: true,
            })
            .then((result) => {
              if (result.isConfirmed) {
                saveExchangeRate(values).then((response) => {
                  Swal.mixin({
                    customClass: {
                      confirmButton: "btn btn-success btn-md btn-pill btn-bold",
                    },
                    buttonsStyling: false,
                  }).fire({
                    title: "Saved",
                    html: response.data.message,
                    icon: "success",
                    showCloseButton: true,
                    confirmButtonText: `<i class="fas fa-check-circle"></i> Okay, noted`,
                  });
                  onConfirmSave();
                });
              } else if (result.dismiss === Swal.DismissReason.cancel) {
                Swal.mixin({
                  customClass: {
                    confirmButton: "btn btn-success btn-md btn-pill btn-bold",
                  },
                  buttonsStyling: false,
                }).fire({
                  title: "Cancelled",
                  text: "No change has been made",
                  icon: "info",
                  showCloseButton: true,
                  confirmButtonText: `<i class="fas fa-check-circle"></i> Okay, noted`,
                });
              }
            });
        }}
      >
        {({
          dirty,
          values,
          status,
          errors,
          touched,
          setFieldValue,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
        }) => (
          <form
            noValidate={true}
            autoComplete="off"
            className="kt-form"
            onSubmit={handleSubmit}
            onKeyDown={onPreventEnterSubmitting}
          >
            {values.ID > 0 ? (
              <FormTextField
                label="Exchange From Foreign Currency"
                name="exCurrCode"
                readonly
              />
            ) : (
              <FormTextField
                label="Exchange From Foreign Currency"
                name="exCurrCode"
                searchButton
                onSearchClick={() => {
                  setOpenSearchCurrency(true);
                }}
                readonly
              />
            )}
            <FormTextField
              label="Exchange To Base Currency"
              name="toCurrCode"
              readonly
            />
            <FormDateField
              label="Valid From"
              name="dateValidFrom"
              readonly={values.ID ? true : false}
            />
            <div className="row">
              <div className="col-sm-6 col-md-6 col-lg-6">
                <FormTextField
                  label={`Exchange Rate ${
                    values.exCurrID > 0 ? values.exCurrCode : "-"
                  } ➡ ${values.toCurrCode}`}
                  name="exRateToBaseCurr"
                  currencyField
                  currencyCode=" "
                  fullDecimals={true}
                  onBlur={(value) => {
                    const { exRateToForeignCurr } = setOppositeExchangeRate(
                      value,
                      "exRateToBaseCurr",
                      values
                    );
                    setFieldValue("exRateToForeignCurr", exRateToForeignCurr);
                  }}
                />
              </div>
              <div className="col-sm-6 col-md-6 col-lg-6">
                <FormTextField
                  label={`Exchange Rate ${values.toCurrCode} ➡ ${
                    values.exCurrID > 0 ? values.exCurrCode : "-"
                  }`}
                  name="exRateToForeignCurr"
                  currencyField
                  currencyCode=" "
                  fullDecimals={true}
                  readonly
                />
              </div>
            </div>

            <DialogActions>
              <button
                type="button"
                className="btn btn-label-danger btn-md btn-pill btn-bold"
                onClick={onCloseDialog}
              >
                <i className="fas fa-window-close"></i>
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-success btn-md btn-pill btn-bold"
                disabled={isSubmitting}
              >
                <i className="fas fa-save"></i>
                Save
              </button>
            </DialogActions>
            <DialogLayout
              open={openSearchCurrency}
              title="Search Currency"
              onCloseDialog={() => {
                setOpenSearchCurrency(false);
              }}
              maxWIdth="sm"
            >
              <SearchCurrency
                onSelectCurrency={(currency) => {
                  setFieldValue("exCurrID", currency.ID);
                  setFieldValue("exCurrCode", currency.currencyCode);
                  setOpenSearchCurrency(false);
                }}
              />
              <DialogActions>
                <button
                  type="button"
                  className="btn btn-label-danger btn-md btn-pill btn-bold"
                  onClick={() => {
                    setOpenSearchCurrency(false);
                  }}
                >
                  <i className="fas fa-window-close"></i>
                  Close
                </button>
              </DialogActions>
            </DialogLayout>
          </form>
        )}
      </Formik>
    </React.Fragment>
  );
};

const setOppositeExchangeRate = (value, propertyName, values) => {
  if (isNaN(value)) {
    value = 0;
  }
  let exRateToBaseCurr = parseFloat(values.exRateToBaseCurr);
  let exRateToForeignCurr = parseFloat(values.exRateToForeignCurr);

  switch (propertyName) {
    case "exRateToBaseCurr":
      if (exRateToBaseCurr > 0) {
        exRateToForeignCurr = 1 / exRateToBaseCurr;
      } else {
        exRateToForeignCurr = 0;
      }
      break;
    default:
      break;
  }
  return {
    exRateToForeignCurr: exRateToForeignCurr,
  };
};

export default ExchangeRateForm;
